import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Text from "../../common/typography/text";
import SectionHeader from "../../common/section-header/index.js";
import TagIcon from "../../icons/tag-icon";
import SendTagEmailIcon from "../../icons/send-tag-email-icon";
import profileImage from "../../assets/icons/profileImage.png";
import { formattedDate, formattedTime } from "../Utils/Helpers";
import ActionsMenuComponent from "../../common/actions-menu-component";
import Button from "../../common/button/button";
import { Checkbox } from "antd";
import ModalView from "../../common/modal";
import Swal from "sweetalert2";
import APIService from "../../Service/APIService";

const TAG_PATTERN = /@[^@\s,.!?;:\n\r)]+/g;
const MENTION_LIMIT = 8;

const getUserDisplayName = (user) => {
  if (!user) return "";
  const direct =
    user?.Name ??
    user?.UserName ??
    user?.FullName ??
    user?.DisplayName ??
    user?.displayName;
  const firstName = user?.FirstName ?? user?.firstName;
  const lastName = user?.LastName ?? user?.lastName;
  const composed = [firstName, lastName].filter(Boolean).join(" ");
  const email = user?.Email ?? user?.email ?? user?.UserEmail ?? user?.UserMail;
  return (direct || composed || email || "").trim();
};

const getUserSecondary = (user) =>
  user?.Email ?? user?.email ?? user?.UserEmail ?? user?.UserMail ?? "";

const getUserKey = (user, fallback) =>
  user?.ID ?? user?.UserID ?? user?.Email ?? user?.email ?? fallback;

const getMentionMatch = (text, caretPos) => {
  if (caretPos == null) return null;
  const upto = text.slice(0, caretPos);
  const atIndex = upto.lastIndexOf("@");
  if (atIndex === -1) return null;
  if (atIndex > 0 && !/\s/.test(upto[atIndex - 1])) return null;
  const query = upto.slice(atIndex + 1);
  if (query.includes("\n")) return null;
  if (/\s/.test(query)) return null;
  return { start: atIndex, end: caretPos, query };
};

const extractTaggedUsers = (text) => {
  if (!text) return [];
  const matches = text.match(TAG_PATTERN);
  if (!matches) return [];
  const cleaned = matches
    .map((tag) => tag.replace(/^@/, "").trim())
    .filter(Boolean);
  return Array.from(new Set(cleaned));
};

const renderCommentText = (text) => {
  if (!text) return "";
  const regex = TAG_PATTERN;
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }
    nodes.push(
      <span key={`${start}-${match[0]}`} className="comment-with-tag-text">
        {match[0]}
      </span>
    );
    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : text;
};

const mapTaggedUsersToDetails = (taggedNames, scopeItems) => {
  if (!Array.isArray(taggedNames) || !Array.isArray(scopeItems)) return [];
  const byLabel = new Map(
    scopeItems.map((item) => [item.label.toLowerCase(), item.user])
  );
  const seen = new Set();
  return taggedNames.reduce((acc, name) => {
    const key = name.toLowerCase();
    if (seen.has(key)) return acc;
    seen.add(key);
    const user = byLabel.get(key);
    if (user) acc.push(user);
    return acc;
  }, []);
};

const getCommentTimestamp = (comment) => {
  const raw =
    comment?.CommentCreatedAt ?? comment?.CreatedAt ?? comment?.createdAt;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortComments = (items) =>
  [...items].sort((a, b) => getCommentTimestamp(b) - getCommentTimestamp(a));

const normalizeId = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const CommentsSection = (props) => {
  const { invoiceId: invoiceIdProp, invoiceComments, userData } = props;
  const params = useParams();
  const invoiceId = invoiceIdProp ?? params?.id;
  const listRef = useRef(null);
  const addInputRef = useRef(null);
  const editInputRef = useRef(null);
  const [comments, setComments] = useState(() =>
    Array.isArray(invoiceComments) ? sortComments(invoiceComments) : []
  );
  const [scopeUsers, setScopeUsers] = useState([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isNotifyCheck, setIsNotifyCheck] = useState(false);
  const [isEditNotifyCheck, setIsEditNotifyCheck] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [activeComment, setActiveComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [addCommentNoteText, setAddCommentNoteText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [addMentionState, setAddMentionState] = useState({
    open: false,
    items: [],
    index: 0,
    range: null,
    query: "",
  });
  const [editMentionState, setEditMentionState] = useState({
    open: false,
    items: [],
    index: 0,
    range: null,
    query: "",
  });

  const currentUserId = useMemo(
    () =>
      normalizeId(
        userData?.UserID ?? userData?.ID ?? userData?.userId ?? userData?.id
      ),
    [userData]
  );

  const canManageComment = (comment) => {
    const commentUserId = normalizeId(
      comment?.CommentByUserID ?? comment?.CommentByUserId ?? comment?.commentByUserId
    );
    return Boolean(currentUserId && commentUserId && currentUserId === commentUserId);
  };

  const normalizedScopeUsers = useMemo(() => {
    if (!Array.isArray(scopeUsers)) return [];
    const seen = new Set();
    return scopeUsers.reduce((acc, user) => {
      const label = getUserDisplayName(user);
      if (!label) return acc;
      const secondary = getUserSecondary(user);
      const key = getUserKey(user, label);
      if (seen.has(key)) return acc;
      seen.add(key);
      acc.push({
        key,
        label,
        secondary,
        search: `${label} ${secondary}`.toLowerCase(),
        user,
      });
      return acc;
    }, []);
  }, [scopeUsers]);

  useEffect(() => {
    if (Array.isArray(invoiceComments)) {
      setComments(sortComments(invoiceComments));
    }
  }, [invoiceComments]);

  const updateMentionState = (text, caretPos, setState) => {
    const match = getMentionMatch(text, caretPos);
    if (!match) {
      setState({
        open: false,
        items: [],
        index: 0,
        range: null,
        query: "",
      });
      return;
    }
    const queryLower = match.query.toLowerCase();
    const filtered = (queryLower
      ? normalizedScopeUsers.filter((item) =>
          item.search.includes(queryLower)
        )
      : normalizedScopeUsers
    ).slice(0, MENTION_LIMIT);

    setState({
      open: true,
      items: filtered,
      index: 0,
      range: match,
      query: match.query,
    });
  };

  const applyMention = (entry, mentionState, setState, text, setText, ref) => {
    if (!entry || !mentionState?.range) return;
    const { start, end } = mentionState.range;
    const prefix = text.slice(0, start);
    const suffix = text.slice(end);
    const insert = `@${entry.label} `;
    const nextText = `${prefix}${insert}${suffix}`;
    setText(nextText);
    setState({
      open: false,
      items: [],
      index: 0,
      range: null,
      query: "",
    });
    const caretPos = prefix.length + insert.length;
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus();
        ref.current.setSelectionRange(caretPos, caretPos);
      }
    });
  };

  const fetchScopeUsers = async () => {
    if (!invoiceId) return;
    setIsUsersLoading(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `GetInvoiceScopeUsers?InvoiceID=${invoiceId}`
      );
      const fetched =
        response?.data?.data?.users ??
        response?.data?.data?.Users ??
        response?.data?.data;
      if (Array.isArray(fetched)) {
        setScopeUsers(fetched);
      } else {
        setScopeUsers([]);
      }
    } catch (error) {
      setScopeUsers([]);
      Swal.fire({
        icon: "error",
        title: "Users load failed",
        text: "Unable to fetch users in scope. Please try again.",
      });
    } finally {
      setIsUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchScopeUsers();
  }, [invoiceId]);

  useEffect(() => {
    if (addMentionState.range) {
      const caretPos = addInputRef.current?.selectionStart;
      updateMentionState(
        addCommentNoteText,
        caretPos ?? addCommentNoteText.length,
        setAddMentionState
      );
    }
    if (editMentionState.range) {
      const caretPos = editInputRef.current?.selectionStart;
      updateMentionState(
        editCommentText,
        caretPos ?? editCommentText.length,
        setEditMentionState
      );
    }
  }, [normalizedScopeUsers]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    checkOverflow();

    window.addEventListener("resize", checkOverflow);
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => {
      window.removeEventListener("resize", checkOverflow);
      ro.disconnect();
    };
  }, [comments, isLoading]);

  const fetchComments = async () => {
    if (!invoiceId) return;
    setIsLoading(true);
    try {
      const apiServe = new APIService();
      let response = await apiServe.getDataFromBackend(
        `getinvoicecomments?InvoiceID=${invoiceId}`
      );
      let fetched = response?.data?.data;

      if (Array.isArray(fetched)) {
        setComments(sortComments(fetched));
      } else if (!Array.isArray(invoiceComments)) {
        setComments([]);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Comments load failed",
        text: "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [invoiceId]);

  const handleAddMentionSelect = (entry) => {
    applyMention(
      entry,
      addMentionState,
      setAddMentionState,
      addCommentNoteText,
      setAddCommentNoteText,
      addInputRef
    );
  };

  const handleEditMentionSelect = (entry) => {
    applyMention(
      entry,
      editMentionState,
      setEditMentionState,
      editCommentText,
      setEditCommentText,
      editInputRef
    );
  };

  const handleMentionNavigation = (
    event,
    mentionState,
    setState,
    onSelect
  ) => {
    if (!mentionState.open) return false;
    const itemsCount = mentionState.items.length;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (itemsCount === 0) return true;
      setState((prev) => ({
        ...prev,
        index: Math.min(prev.index + 1, itemsCount - 1),
      }));
      return true;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (itemsCount === 0) return true;
      setState((prev) => ({
        ...prev,
        index: Math.max(prev.index - 1, 0),
      }));
      return true;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      if (itemsCount > 0) {
        event.preventDefault();
        onSelect(mentionState.items[mentionState.index]);
      }
      return true;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setState((prev) => ({ ...prev, open: false }));
      return true;
    }
    return false;
  };

  const handleAddInputChange = (event) => {
    const value = event.target.value;
    setAddCommentNoteText(value);
    updateMentionState(
      value,
      event.target.selectionStart,
      setAddMentionState
    );
  };

  const handleAddInputCaretChange = (event) => {
    updateMentionState(
      event.target.value,
      event.target.selectionStart,
      setAddMentionState
    );
  };

  const handleAddInputBlur = () => {
    setTimeout(() => {
      setAddMentionState((prev) => ({ ...prev, open: false }));
    }, 150);
  };

  const handleEditInputChange = (event) => {
    const value = event.target.value;
    setEditCommentText(value);
    updateMentionState(
      value,
      event.target.selectionStart,
      setEditMentionState
    );
  };

  const handleEditInputCaretChange = (event) => {
    updateMentionState(
      event.target.value,
      event.target.selectionStart,
      setEditMentionState
    );
  };

  const handleEditInputBlur = () => {
    setTimeout(() => {
      setEditMentionState((prev) => ({ ...prev, open: false }));
    }, 150);
  };

  const handleAddComment = async () => {
    if (isSubmitting) return;
    const trimmed = addCommentNoteText?.trim();
    if (!trimmed) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter comment content",
      });
      return;
    }
    if (!invoiceId) {
      Swal.fire({
        icon: "error",
        title: "Missing invoice",
        text: "Invoice data is not available. Please try again.",
      });
      return;
    }

    const taggedUsers = extractTaggedUsers(trimmed);
    const taggedUserDetails = mapTaggedUsersToDetails(
      taggedUsers,
      normalizedScopeUsers
    );
    const payload = {
      InvoiceID: invoiceId,
      CommentContent: trimmed,
      Comment: trimmed,
      TaggedUsers: taggedUserDetails,
      TaggedUser: taggedUsers[0] ?? null,
      NotifyByEmail: isNotifyCheck,
    };

    setIsSubmitting(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.addDataToBackend(
        Object.fromEntries(
          Object.entries(payload).filter(([, value]) => value !== undefined)
        ),
        "addinvoicecomment"
      );
      if (response?.status === 200 || response?.status === 201) {
        const created =
          response?.data?.data?.comment ?? response?.data?.data ?? null;
        if (created && typeof created === "object") {
          setComments((prev) => sortComments([created, ...prev]));
        } else {
          await fetchComments();
        }
        setAddCommentNoteText("");
        setAddMentionState({
          open: false,
          items: [],
          index: 0,
          range: null,
          query: "",
        });
        setIsNotifyCheck(false);
        Swal.fire("Added!", "Comment added successfully", "success");
      } else {
        Swal.fire({
          icon: "error",
          title: "Comment add failed",
          text: "Please try again.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Comment add failed",
        text: "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditOpen = (comment) => {
    if (!comment) return;
    if (!canManageComment(comment)) return;
    setActiveComment(comment);
    setEditCommentText(comment?.Comment ?? comment?.commentText ?? "");
    setIsEditNotifyCheck(Boolean(comment?.NotifyByEmail));
    setEditMentionState({
      open: false,
      items: [],
      index: 0,
      range: null,
      query: "",
    });
    setIsEditModal(true);
  };

  const handleEditClose = () => {
    setIsEditModal(false);
    setActiveComment(null);
    setEditCommentText("");
    setIsEditNotifyCheck(false);
    setEditMentionState({
      open: false,
      items: [],
      index: 0,
      range: null,
      query: "",
    });
    setIsUpdating(false);
  };

  const handleUpdateComment = async () => {
    if (!activeComment || isUpdating) return;
    const trimmed = editCommentText?.trim();
    if (!trimmed) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please enter comment content",
      });
      return;
    }
    if (!invoiceId) {
      Swal.fire({
        icon: "error",
        title: "Missing invoice",
        text: "Invoice data is not available. Please try again.",
      });
      return;
    }
    const commentId = activeComment?.ID ?? activeComment?.CommentID;
    if (!commentId) {
      Swal.fire({
        icon: "error",
        title: "Missing comment",
        text: "Comment data is not available. Please try again.",
      });
      return;
    }

    const taggedUsers = extractTaggedUsers(trimmed);
    const payload = {
      InvoiceID: invoiceId,
      CommentID: commentId,
      CommentContent: trimmed,
      Comment: trimmed,
      TaggedUsers: taggedUsers,
      TaggedUser: taggedUsers[0] ?? null,
      NotifyByEmail: isEditNotifyCheck,
    };

    setIsUpdating(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.addDataToBackend(
        Object.fromEntries(
          Object.entries(payload).filter(([, value]) => value !== undefined)
        ),
        "UpdateInvoiceComment"
      );
      if (response?.status === 200 || response?.status === 201) {
        setComments((prev) =>
          sortComments(
            prev.map((item) =>
              (item?.ID ?? item?.CommentID) === commentId
                ? {
                    ...item,
                    Comment: trimmed,
                    TaggedUsers: taggedUsers,
                    taggedUser: taggedUsers[0] ?? item?.taggedUser,
                  }
                : item
            )
          )
        );
        Swal.fire("Updated!", "Comment updated successfully", "success").then(
          handleEditClose
        );
      } else {
        Swal.fire({
          icon: "error",
          title: "Comment update failed",
          text: "Please try again.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Comment update failed",
        text: "Please try again.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteComment = (comment) => {
    if (!comment) return;
    if (!canManageComment(comment)) return;
    Swal.fire({
      title: "Are you sure?",
      text: "The comment will get deleted",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }
      const commentId = comment?.ID ?? comment?.CommentID ?? comment?.InvoiceCommentID;
      if (!commentId) {
        Swal.fire({
          icon: "error",
          title: "Missing comment",
          text: "Comment data is not available. Please try again.",
        });
        return;
      }

      try {
        const apiServe = new APIService();
        const response = await apiServe.addDataToBackend(
          {},
          `DeleteInvoiceComment?InvoiceCommentID=${commentId}`
        );
        if (response?.status === 200 || response?.status === 201) {
          setComments((prev) =>
            prev.filter(
              (item) => (item?.ID ?? item?.CommentID) !== commentId
            )
          );
          Swal.fire("Deleted!", "Comment deleted successfully", "success");
        } else {
          Swal.fire({
            icon: "error",
            title: "Comment delete failed",
            text: "Please try again.",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Comment delete failed",
          text: "Please try again.",
        });
      }
    });
  };

  const totalComments = comments?.length ?? 0;

  return (
    <div className="comments-section-container">
      <div className="comments-input-parent-container">
        <SectionHeader
          title={`Comments (${totalComments})`}
          showButton={false}
          style={{ width: "100%", marginTop: 0, boxSizing: "border-box" }}
        />
        <div className="comments-input-container">
          <div className="comments-input-textarea-wrapper">
            <textarea
              ref={addInputRef}
              className="comments-input-textarea"
              placeholder="Add a comment"
              maxLength={1000}
              value={addCommentNoteText}
              onChange={handleAddInputChange}
              onKeyDown={(event) => {
                const handled = handleMentionNavigation(
                  event,
                  addMentionState,
                  setAddMentionState,
                  handleAddMentionSelect
                );
                if (handled) return;
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                  handleAddComment();
                }
              }}
              onKeyUp={handleAddInputCaretChange}
              onClick={handleAddInputCaretChange}
              onBlur={handleAddInputBlur}
            />
            {addMentionState.open && (
              <div className="comments-mention-dropdown" role="listbox">
                {isUsersLoading && (
                  <Text variant="tinyText" color="lightText">
                    Loading users...
                  </Text>
                )}
                {!isUsersLoading && addMentionState.items.length === 0 && (
                  <Text variant="tinyText" color="lightText">
                    No users found
                  </Text>
                )}
                {!isUsersLoading &&
                  addMentionState.items.map((entry, index) => (
                    <button
                      type="button"
                      key={entry.key}
                      role="option"
                      aria-selected={index === addMentionState.index}
                      className={
                        "comments-mention-item" +
                        (index === addMentionState.index
                          ? " is-active"
                          : "")
                      }
                      onMouseDown={(event) => {
                        event.preventDefault();
                        handleAddMentionSelect(entry);
                      }}
                    >
                      <span className="comments-mention-name">
                        {entry.label}
                      </span>
                      {entry.secondary && (
                        <span className="comments-mention-secondary">
                          {entry.secondary}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            )}
          </div>
          <div className="commments-input-footer-container">
            <div className="comments-input-footer-left-container">
              <div className="comments-input-footer-left-inner-container">
                <TagIcon />
                <Text variant="tinyText">
                  Tagged users will be notified via email
                </Text>
              </div>
              <div className="comments-individual-header-left">
                {/* <Checkbox
                  checked={isNotifyCheck}
                  onClick={() => setIsNotifyCheck(!isNotifyCheck)}
                />
                <Text variant="tinyText">Notify via email</Text> */}
              </div>
            </div>
            <button
              type="button"
              className="cursor-pointer-style"
              onClick={handleAddComment}
              disabled={isSubmitting || !addCommentNoteText?.trim()}
              aria-label="Add comment"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                opacity:
                  isSubmitting || !addCommentNoteText?.trim() ? 0.6 : 1,
              }}
            >
              <SendTagEmailIcon />
            </button>
          </div>
        </div>
      </div>

      <div className="comments-list-parent-wrapper">
        <div
          className={
            "comments-list-parent-container" +
            (isOverflowing ? " is-overflowing" : "")
          }
          ref={listRef}
        >
          {isLoading && (
            <Text variant="smallText" color="lightText">
              Loading comments...
            </Text>
          )}
          {!isLoading && comments.length === 0 && (
            <Text variant="smallText" color="lightText">
              No comments yet.
            </Text>
          )}
          {comments?.map((comment, index) => {
            const createdAt =
              comment?.CommentCreatedAt ??
              comment?.CreatedAt ??
              comment?.createdAt;
            const commentTime = formattedTime(createdAt);
            const commentDate = formattedDate(createdAt);
            const commentText =
              comment?.Comment ?? comment?.commentText ?? "";
            const commentUser =
              comment?.CommentByUserName ?? comment?.createdBy ?? "Unknown";
            const isOwner = canManageComment(comment);

            return (
              <div
                className="comments-list-individual-container"
                key={
                  comment?.ID ??
                  comment?.CommentID ??
                  `${commentTime}-${index}`
                }
              >
                <div className="comments-individual-header">
                  <div className="comments-individual-header-left">
                    <div className="comments-individual-header-left">
                      <img
                        src={profileImage}
                        className="profile-image-container-small"
                        alt="profile"
                      />
                      <Text variant="smallText">{commentUser}</Text>
                    </div>
                    <div className="invoice-page-ellipse" />
                    <Text variant="smallText" color="lightText">
                      {commentTime}
                    </Text>
                  </div>
                  <div className="comments-individual-header-right">
                    <Text variant="smallText" color="lightText">
                      {commentDate}
                    </Text>
                    {isOwner && (
                      <ActionsMenuComponent
                        onEdit={() => handleEditOpen(comment)}
                        handleDeleteComment={() => handleDeleteComment(comment)}
                      />
                    )}
                  </div>
                </div>
                <div className="comments-individual-body-container">
                  <div className="comments-individual-body-text-container">
                    <Text variant="smallText">
                      {renderCommentText(commentText)}
                    </Text>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isEditModal && (
        <ModalView
          isOpen={isEditModal}
          onClose={handleEditClose}
          modalTitle="Edit comment"
          textInputComponent={
            <div className="field-group-wrapper">
              <div className="add-note-model-input-container">
                <div className="comments-input-textarea-wrapper">
                  <textarea
                    ref={editInputRef}
                    className="add-note-textarea"
                    placeholder="Edit a comment"
                    maxLength={1000}
                    value={editCommentText}
                    onChange={handleEditInputChange}
                    onKeyDown={(event) => {
                      const handled = handleMentionNavigation(
                        event,
                        editMentionState,
                        setEditMentionState,
                        handleEditMentionSelect
                      );
                      if (handled) return;
                      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                        handleUpdateComment();
                      }
                    }}
                    onKeyUp={handleEditInputCaretChange}
                    onClick={handleEditInputCaretChange}
                    onBlur={handleEditInputBlur}
                  />
                  {editMentionState.open && (
                    <div className="comments-mention-dropdown" role="listbox">
                      {isUsersLoading && (
                        <Text variant="tinyText" color="lightText">
                          Loading users...
                        </Text>
                      )}
                      {!isUsersLoading && editMentionState.items.length === 0 && (
                        <Text variant="tinyText" color="lightText">
                          No users found
                        </Text>
                      )}
                      {!isUsersLoading &&
                        editMentionState.items.map((entry, index) => (
                          <button
                            type="button"
                            key={entry.key}
                            role="option"
                            aria-selected={index === editMentionState.index}
                            className={
                              "comments-mention-item" +
                              (index === editMentionState.index
                                ? " is-active"
                                : "")
                            }
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleEditMentionSelect(entry);
                            }}
                          >
                            <span className="comments-mention-name">
                              {entry.label}
                            </span>
                            {entry.secondary && (
                              <span className="comments-mention-secondary">
                                {entry.secondary}
                              </span>
                            )}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <div className="comments-individual-header-left">
                  <Checkbox
                    checked={isEditNotifyCheck}
                    onClick={() =>
                      setIsEditNotifyCheck(!isEditNotifyCheck)
                    }
                  />
                  <Text variant="smallText">Notify via email</Text>
                </div>
              </div>
            </div>
          }
          footerButtonWrapper={
            <div
              className="footer-button-wrapper-flex-end"
              style={{ gap: "12px" }}
            >
              <Button
                variant="table"
                subVariant="outline"
                onClick={handleEditClose}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                variant="table"
                subVariant="primary"
                onClick={handleUpdateComment}
                disabled={isUpdating || !editCommentText?.trim()}
              >
                Save
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
};

export default CommentsSection;
